import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Receipt, IndianRupee, Eraser, BellRing } from "lucide-react";
import { inr, PLAN_META, STATUS_META } from "./mockData";

export function FlatsBoard({ flats, readOnly = false, onRecordFor, onWriteOff }) {
  const [selected, setSelected] = useState(null);
  const [confirmWriteOff, setConfirmWriteOff] = useState(false);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 text-[11px] text-stone-500">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${m.dot}`} /> {m.label}</span>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="flats-board">
        {flats.map((f) => {
          const st = STATUS_META[f.status];
          const owes = f.dueNow + (f.arrears || 0);
          return (
            <button key={f.id} onClick={() => { setSelected(f); setConfirmWriteOff(false); }} data-testid={`flat-card-${f.flat}`}
              className="text-left rounded-2xl border border-stone-200 bg-white p-3.5 hover:border-[#C05640]/50 hover:shadow-sm transition">
              <div className="flex items-center justify-between">
                <span className="font-serif-display font-semibold">{f.flat}</span>
                <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`} />
              </div>
              <div className="text-xs text-stone-500 truncate mt-0.5">{f.name}</div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[10px] bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded-full">{PLAN_META[f.plan].label}</span>
                {owes > 0
                  ? <span className="text-xs font-bold text-red-600">{inr(owes)}</span>
                  : <span className="text-xs font-semibold text-emerald-600">✓ Paid</span>}
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="flat-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-display flex items-center gap-2">
                  {selected.flat} · {selected.name}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-sans font-semibold ${STATUS_META[selected.status].cls}`}>{STATUS_META[selected.status].label}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-stone-50 p-3">
                    <div className="text-[10px] text-stone-500 uppercase tracking-wide">Plan</div>
                    <div className="font-semibold text-xs mt-0.5">{PLAN_META[selected.plan].label}</div>
                    <div className="text-[11px] text-stone-500">{inr(PLAN_META[selected.plan].amount)}/cycle</div>
                  </div>
                  <div className="rounded-xl bg-stone-50 p-3">
                    <div className="text-[10px] text-stone-500 uppercase tracking-wide">Outstanding</div>
                    <div className={`font-semibold text-xs mt-0.5 ${selected.dueNow + (selected.arrears || 0) > 0 ? "text-red-600" : "text-emerald-600"}`}>
                      {inr(selected.dueNow + (selected.arrears || 0))}
                    </div>
                    {selected.arrears > 0 && <div className="text-[11px] text-stone-500">incl. {inr(selected.arrears)} old arrears</div>}
                  </div>
                </div>
                {selected.note && <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">{selected.note}</div>}
                <div>
                  <div className="text-xs font-semibold text-stone-600 mb-1.5 flex items-center gap-1"><Receipt size={13} /> Recent receipts</div>
                  {selected.receipts?.length ? selected.receipts.map((r) => (
                    <div key={r} className="text-xs text-stone-600 py-1.5 border-b border-stone-100 last:border-0">{r}</div>
                  )) : <div className="text-xs text-stone-400">No receipts yet</div>}
                </div>
                {!readOnly && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {selected.status !== "paid" && (
                      <Button size="sm" onClick={() => { onRecordFor(selected.id); setSelected(null); }} data-testid="flat-record-payment-btn"
                        className="bg-[#C05640] hover:bg-[#A8492F] rounded-full">
                        <IndianRupee size={14} className="mr-1" /> Record payment
                      </Button>
                    )}
                    {selected.status !== "paid" && (
                      <Button size="sm" variant="outline" className="rounded-full border-stone-300" data-testid="flat-remind-btn"
                        onClick={() => { toast("Reminder queued — see Reminders tab"); setSelected(null); }}>
                        <BellRing size={14} className="mr-1" /> Remind
                      </Button>
                    )}
                    {selected.arrears > 0 && !confirmWriteOff && (
                      <Button size="sm" variant="ghost" className="rounded-full text-stone-500" data-testid="flat-writeoff-btn"
                        onClick={() => setConfirmWriteOff(true)}>
                        <Eraser size={14} className="mr-1" /> Write off arrears
                      </Button>
                    )}
                  </div>
                )}
                {confirmWriteOff && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3" data-testid="writeoff-confirm-box">
                    <div className="text-xs text-red-800 font-medium">Write off {inr(selected.arrears)} of old arrears for {selected.flat}?</div>
                    <div className="text-[11px] text-red-600 mt-0.5">Recorded in the audit trail with your name & reason. Committee-only action.</div>
                    <input placeholder="Reason (e.g., GB decision 12 May 2026)" data-testid="writeoff-reason-input"
                      className="mt-2 w-full rounded-lg border border-red-200 p-2 text-xs bg-white" />
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 rounded-full text-xs" data-testid="writeoff-confirm-btn"
                        onClick={() => { onWriteOff(selected.id); setSelected(null); toast.success(`Arrears written off for ${selected.flat} — logged in audit trail`); }}>
                        Confirm write-off
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setConfirmWriteOff(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
